import { useEffect, useRef, useState, type DragEvent, type Key } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Alert, { type AlertColor } from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import { ApiError } from '../api/client';
import type { ManagerRole } from '../api/managerApi';
import {
  blockBackdropClose,
  DataTable,
  FormDialogTitle,
  LoadingState,
  useErrorToast,
  type DataTableColumn,
} from '../components/common';
import { useEventContext } from '../contexts/EventContext';
import { categoryService, type Category } from '../services/categoryService';
import { eventService } from '../services/eventService';
import { loadMyManagerProfile } from '../services/managerService';
import {
  participantService,
  type Participant,
  type ParticipantImportFailure,
  type ParticipantImportResult,
} from '../services/participantService';
import { formatMobilePhoneNumber, formatPhoneNumberForDisplay } from '../utils/phoneNumber';

// 생년월일 입력 범위. min/max가 없으면 브라우저 날짜 입력의 연도 칸에 4자리를 넘는 숫자도 입력되는 문제가 있어 지정한다.
const MIN_BIRTH_DATE = '1900-01-01';
const MAX_BIRTH_DATE = new Date().toISOString().slice(0, 10);

const FIELD_LABELS: Record<string, string> = {
  bibNumber: '배번호',
  chipNumber: '칩번호',
  name: '이름',
  gender: '성별',
  category: '종목',
  group: '그룹',
  birth: '생년월일',
  phoneNumber: '전화번호',
};

/** 실패 행 사유를 사람이 읽을 문구로 변환 */
function formatImportFailure(fail: ParticipantImportFailure) {
  const { missing, format, duplicate, notFound } = fail.failedReason;
  const label = (fields: string[]) => fields.map((f) => FIELD_LABELS[f] ?? f).join(', ');
  const parts: string[] = [];
  if (missing.length) parts.push(`누락(${label(missing)})`);
  if (format.length) parts.push(`형식오류(${label(format)})`);
  if (duplicate.length) parts.push(`중복(${label(duplicate)})`);
  if (notFound.length) parts.push(`미등록(${label(notFound)})`);
  return `${fail.failedNum}행 — ${parts.join(' · ')}`;
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// 대회별 생년월일·성별 수집 여부. 수집하면 필수, 수집하지 않으면 입력칸을 숨기고 값을 보내지 않는다.
type CollectFlags = {
  collectBirth: boolean;
  collectGender: boolean;
};

const DEFAULT_COLLECT_FLAGS: CollectFlags = { collectBirth: true, collectGender: true };

function getRequiredParticipantFormErrors(
  form: AddForm,
  hasCategory: boolean,
  collectFlags: CollectFlags,
): ParticipantFormErrors {
  const errors: ParticipantFormErrors = {};

  if (!form.bibNumber) errors.bibNumber = '배번호를 입력해 주세요.';
  if (!form.chipNumber) errors.chipNumber = '칩번호를 입력해 주세요.';
  if (!form.name.trim()) errors.name = '이름을 입력해 주세요.';
  if (collectFlags.collectGender && !form.gender.trim()) errors.gender = '성별을 입력해 주세요.';
  if (!hasCategory) errors.categoryId = '종목을 선택해 주세요.';
  if (!form.group) errors.group = '그룹을 선택해 주세요.';
  if (collectFlags.collectBirth && !form.birth) errors.birth = '생년월일을 입력해 주세요.';

  return errors;
}

function getNumericParticipantFormErrors(form: AddForm): ParticipantFormErrors {
  const errors: ParticipantFormErrors = {};

  if (Number.isNaN(Number(form.bibNumber))) {
    errors.bibNumber = '배번호는 숫자로 입력해 주세요.';
  }
  if (Number.isNaN(Number(form.chipNumber))) {
    errors.chipNumber = '칩번호는 숫자로 입력해 주세요.';
  }

  return errors;
}

function getParticipantServerFieldErrors(error: unknown): ParticipantFormErrors {
  if (!(error instanceof ApiError)) {
    return {};
  }

  const errors: ParticipantFormErrors = {};
  const duplicateField = error.code ? PARTICIPANT_DUPLICATE_ERROR_FIELDS[error.code] : undefined;

  if (duplicateField) {
    errors[duplicateField] = error.message;
  }

  error.fieldErrors.forEach((fieldError) => {
    const field = PARTICIPANT_SERVER_ERROR_FIELD_MAP[fieldError.field];

    if (field) {
      errors[field] = fieldError.reason;
    }
  });

  return errors;
}

type AddForm = {
  bibNumber: string;
  chipNumber: string;
  name: string;
  gender: string;
  categoryId: number | '';
  group: string;
  birth: string;
  phoneNumber: string;
};

type ParticipantFormErrors = Partial<Record<keyof AddForm, string>>;

const emptyAddForm: AddForm = {
  bibNumber: '',
  chipNumber: '',
  name: '',
  gender: '',
  categoryId: '',
  group: '',
  birth: '',
  phoneNumber: '',
};

const PARTICIPANT_DUPLICATE_ERROR_FIELDS: Record<string, keyof AddForm> = {
  '40006002': 'bibNumber',
  '40006003': 'chipNumber',
  '40006004': 'phoneNumber',
  '40006011': 'birth',
  '40006012': 'gender',
};

const PARTICIPANT_SERVER_ERROR_FIELD_MAP: Record<string, keyof AddForm> = {
  bibNumber: 'bibNumber',
  chipNumber: 'chipNumber',
  name: 'name',
  gender: 'gender',
  category: 'categoryId',
  group: 'group',
  birth: 'birth',
  phoneNumber: 'phoneNumber',
};

const PAGE_SIZE = 20;

type DeleteNotice = {
  message: string;
  severity: AlertColor;
};

function buildParticipantDeleteNotice(requestedCount: number, deletedCount: number): DeleteNotice {
  const skippedCount = Math.max(requestedCount - deletedCount, 0);

  if (deletedCount === 0) {
    return {
      severity: 'warning',
      message: '삭제할 수 있는 참가자가 없습니다. 경기 기록이 있는 참가자는 삭제할 수 없습니다.',
    };
  }

  if (skippedCount > 0) {
    return {
      severity: 'info',
      message: `삭제 가능한 참가자 ${deletedCount.toLocaleString()}명은 삭제되었습니다. 경기 기록이 있는 참가자 ${skippedCount.toLocaleString()}명은 삭제되지 않았습니다.`,
    };
  }

  return {
    severity: 'success',
    message: `참가자 ${deletedCount.toLocaleString()}명이 삭제되었습니다.`,
  };
}

const EditIcon = () => (
  <svg
    width={15}
    height={15}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const baseColumns: DataTableColumn<Participant>[] = [
  { key: 'bibNumber', header: '배번호' },
  { key: 'chipNumber', header: '칩번호' },
  { key: 'name', header: '이름' },
  { key: 'gender', header: '성별' },
  { key: 'category', header: '종목' },
  { key: 'ageGroup', header: '나이그룹' },
  { key: 'group', header: '그룹' },
  { key: 'birth', header: '생년월일' },
  {
    key: 'phoneNumber',
    header: '전화번호',
    render: (participant) => formatPhoneNumberForDisplay(participant.phoneNumber ?? undefined),
  },
];

function ParticipantsPage() {
  const queryClient = useQueryClient();
  const { selectedEventId } = useEventContext();
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [draftKeyword, setDraftKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isConfirmDeleteAllOpen, setIsConfirmDeleteAllOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddForm>(emptyAddForm);
  const [addError, setAddError] = useState<string | null>(null);
  const [addFieldErrors, setAddFieldErrors] = useState<ParticipantFormErrors>({});
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ParticipantImportResult | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [originalGroup, setOriginalGroup] = useState('');
  const [originalPhoneNumber, setOriginalPhoneNumber] = useState('');
  const [isConfirmGroupChangeOpen, setIsConfirmGroupChangeOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editForm, setEditForm] = useState<AddForm>(emptyAddForm);
  const [editAgeGroup, setEditAgeGroup] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteNotice, setDeleteNotice] = useState<DeleteNotice | null>(null);
  const [managerRole, setManagerRole] = useState<ManagerRole | null>(null);
  const [collectFlags, setCollectFlags] = useState<CollectFlags>(DEFAULT_COLLECT_FLAGS);
  const [originalHasGender, setOriginalHasGender] = useState(false);
  const [originalHasBirth, setOriginalHasBirth] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showError, errorToast } = useErrorToast();
  const canManageParticipants = managerRole === 'SUPER' || managerRole === 'MIDDLE';
  const canUpdateParticipants = canManageParticipants || managerRole === 'GENERAL';

  useEffect(() => {
    let ignore = false;

    loadMyManagerProfile()
      .then((profile) => {
        if (!ignore) {
          setManagerRole(profile.role);
        }
      })
      .catch(() => {
        if (!ignore) {
          setManagerRole(null);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (selectedEventId == null) return;
    categoryService.list(selectedEventId).then(setCategories).catch(showError);
  }, [selectedEventId, showError]);

  useEffect(() => {
    if (selectedEventId == null) return;
    let ignore = false;
    eventService
      .detail(selectedEventId)
      .then((event) => {
        if (!ignore) {
          setCollectFlags({ collectBirth: event.collectBirth, collectGender: event.collectGender });
        }
      })
      .catch(showError);
    return () => {
      ignore = true;
    };
  }, [selectedEventId, showError]);

  useEffect(() => {
    if (selectedEventId == null) return;
    setIsLoading(true);
    participantService
      .search(selectedEventId, { keyword: appliedKeyword, page, size: PAGE_SIZE })
      .then((res) => {
        setParticipants(res.participants);
        setTotalCount(res.totalCount);
        setSelectedKeys([]);
        // 마지막 페이지의 항목을 모두 지워 페이지가 비면 이전 페이지로 이동
        if (res.participants.length === 0 && res.totalCount > 0 && page > 0) {
          setPage(page - 1);
        }
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
  }, [selectedEventId, appliedKeyword, page, refreshKey, showError]);

  const reloadCurrentPage = async () => {
    if (selectedEventId == null) return null;
    const result = await participantService.search(selectedEventId, {
      keyword: appliedKeyword,
      page,
      size: PAGE_SIZE,
    });
    setParticipants(result.participants);
    setTotalCount(result.totalCount);
    setSelectedKeys([]);
    if (result.participants.length === 0 && result.totalCount > 0 && page > 0) {
      setPage(page - 1);
    }
    return result;
  };

  const getParticipantCount = async () => {
    if (selectedEventId == null) return 0;
    const result = await participantService.search(selectedEventId, { page: 0, size: 1 });
    return result.totalCount;
  };

  const handleDeleteSelected = async () => {
    if (!canManageParticipants || selectedEventId == null || selectedKeys.length === 0) return;
    try {
      const requestedCount = selectedKeys.length;
      const beforeTotalCount = totalCount;
      await participantService.remove(selectedEventId, selectedKeys.map(Number));
      const result = await reloadCurrentPage();
      const afterTotalCount = result?.totalCount ?? beforeTotalCount;
      const deletedCount = Math.max(0, Math.min(requestedCount, beforeTotalCount - afterTotalCount));
      setIsConfirmDeleteOpen(false);
      setDeleteNotice(buildParticipantDeleteNotice(requestedCount, deletedCount));
    } catch (error) {
      showError(error);
    }
  };

  const handleDeleteAll = async () => {
    if (!canManageParticipants || selectedEventId == null) return;
    try {
      const beforeTotalCount = await getParticipantCount();
      await participantService.removeAll(selectedEventId);
      const afterTotalCount = await getParticipantCount();
      const deletedCount = Math.max(0, beforeTotalCount - afterTotalCount);
      setIsConfirmDeleteAllOpen(false);
      setPage(0);
      setDeleteNotice(buildParticipantDeleteNotice(beforeTotalCount, deletedCount));
      setRefreshKey((key) => key + 1);
    } catch (error) {
      showError(error);
    }
  };

  const applySearch = () => {
    setPage(0);
    setAppliedKeyword(draftKeyword.trim());
  };

  const resetSearch = () => {
    setDraftKeyword('');
    setAppliedKeyword('');
    setPage(0);
  };

  const openAdd = () => {
    if (!canManageParticipants) return;
    setAddForm(emptyAddForm);
    setAddError(null);
    setAddFieldErrors({});
    setIsAddOpen(true);
  };

  const updateAddField = <K extends keyof AddForm>(key: K, value: AddForm[K]) => {
    setAddForm((form) => ({ ...form, [key]: value }));
    setAddFieldErrors((errors) => ({ ...errors, [key]: undefined }));
    setAddError(null);
  };

  const selectedAddCategory = categories.find((c) => c.id === addForm.categoryId) ?? null;
  const addGroupOptions = selectedAddCategory?.groups ?? [];

  const handleCreate = async () => {
    if (!canManageParticipants || selectedEventId == null) return;
    const category = categories.find((c) => c.id === addForm.categoryId);
    const requiredErrors = getRequiredParticipantFormErrors(addForm, Boolean(category), collectFlags);
    if (Object.keys(requiredErrors).length > 0) {
      setAddFieldErrors(requiredErrors);
      setAddError('입력되지 않은 항목을 확인해 주세요.');
      return;
    }
    const bibNumber = Number(addForm.bibNumber);
    const chipNumber = Number(addForm.chipNumber);
    const numericErrors = getNumericParticipantFormErrors(addForm);
    if (Object.keys(numericErrors).length > 0) {
      setAddFieldErrors(numericErrors);
      setAddError('숫자 입력 항목을 확인해 주세요.');
      return;
    }
    if (!category) return;

    try {
      await participantService.create(selectedEventId, {
        bibNumber,
        chipNumber,
        name: addForm.name.trim(),
        gender: collectFlags.collectGender ? addForm.gender.trim() : undefined,
        category: category.categoryName,
        group: addForm.group,
        birth: collectFlags.collectBirth ? addForm.birth : undefined,
        phoneNumber: formatMobilePhoneNumber(addForm.phoneNumber) || undefined,
      });
      setIsAddOpen(false);
      setPage(0);
      setRefreshKey((key) => key + 1);
    } catch (error) {
      const fieldErrors = getParticipantServerFieldErrors(error);

      if (Object.keys(fieldErrors).length > 0) {
        setAddFieldErrors(fieldErrors);
        setAddError('오류가 발생한 항목을 확인해 주세요.');
        return;
      }

      setAddError(error instanceof Error ? error.message : '참가자 추가 중 오류가 발생했습니다.');
    }
  };

  const openImport = () => {
    if (!canManageParticipants) return;
    setImportFile(null);
    setImportResult(null);
    setIsImportOpen(true);
  };

  const pickImportFile = (file: File | null | undefined) => {
    if (!file) return;
    setImportFile(file);
    setImportResult(null);
  };

  const handleImportDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    pickImportFile(event.dataTransfer.files?.[0]);
  };

  const handleImport = async () => {
    if (!canManageParticipants || selectedEventId == null || importFile == null) return;
    setIsImporting(true);
    try {
      const result = await participantService.importFile(selectedEventId, importFile);
      setImportResult(result);
      if (result.insertedCount > 0) {
        setPage(0);
        setRefreshKey((key) => key + 1);
      }
    } catch (error) {
      showError(error);
    } finally {
      setIsImporting(false);
    }
  };

  const handleExport = async () => {
    if (!canManageParticipants || selectedEventId == null) return;
    setIsExporting(true);
    try {
      const blob = await participantService.exportCsv(selectedEventId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = '참가자_목록.csv';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      showError(error);
    } finally {
      setIsExporting(false);
    }
  };

  const openEdit = (participant: Participant) => {
    if (!canUpdateParticipants) return;
    const category = categories.find((c) => c.categoryName === participant.category);
    setEditingId(participant.id);
    setOriginalGroup(participant.group);
    const phone = participant.phoneNumber ?? '';
    setOriginalPhoneNumber(phone);
    setOriginalHasGender(Boolean(participant.gender));
    setOriginalHasBirth(Boolean(participant.birth));
    setEditAgeGroup(participant.ageGroup ?? '');
    setEditError(null);
    setEditForm({
      bibNumber: String(participant.bibNumber),
      chipNumber: String(participant.chipNumber),
      name: participant.name,
      gender: participant.gender ?? '',
      categoryId: category?.id ?? '',
      group: participant.group,
      birth: participant.birth ?? '',
      phoneNumber: phone.includes('*') ? phone : formatMobilePhoneNumber(phone),
    });
    setIsEditOpen(true);
  };

  const updateEditField = <K extends keyof AddForm>(key: K, value: AddForm[K]) => {
    setEditForm((form) => ({ ...form, [key]: value }));
  };

  const selectedEditCategory = categories.find((c) => c.id === editForm.categoryId) ?? null;
  const editGroupOptions = selectedEditCategory?.groups ?? [];

  const handleUpdate = () => {
    if (!canUpdateParticipants || selectedEventId == null || editingId == null) return;
    const category = categories.find((c) => c.id === editForm.categoryId);
    if (
      !editForm.bibNumber ||
      !editForm.chipNumber ||
      !editForm.name.trim() ||
      !category ||
      !editForm.group
    ) {
      setEditError('필수 항목을 입력해 주세요.');
      return;
    }
    // 수집하는 항목은 기존 값을 지울 수 없다. (원래 비어 있던 값은 비워둔 채 다른 항목만 수정할 수 있다)
    if (collectFlags.collectGender && originalHasGender && !editForm.gender.trim()) {
      setEditError('성별을 입력해 주세요.');
      return;
    }
    if (collectFlags.collectBirth && originalHasBirth && !editForm.birth) {
      setEditError('생년월일을 입력해 주세요.');
      return;
    }
    const bibNumber = Number(editForm.bibNumber);
    const chipNumber = Number(editForm.chipNumber);
    if (Number.isNaN(bibNumber) || Number.isNaN(chipNumber)) {
      setEditError('배번호·칩번호는 숫자여야 합니다.');
      return;
    }
    setEditError(null);
    if (editForm.group !== originalGroup) {
      setIsConfirmGroupChangeOpen(true);
      return;
    }
    void submitUpdate();
  };

  const submitUpdate = async () => {
    if (!canUpdateParticipants || selectedEventId == null || editingId == null || isUpdating) return;
    const category = categories.find((c) => c.id === editForm.categoryId);
    if (!category) return;
    const bibNumber = Number(editForm.bibNumber);
    const chipNumber = Number(editForm.chipNumber);
    // 마스킹된 전화번호(권한상 '****')는 그대로 보내면 원본이 훼손되므로 전송에서 제외해 기존 값을 유지한다.
    const phoneMasked = editForm.phoneNumber.includes('*');
    const phoneNumber = formatMobilePhoneNumber(editForm.phoneNumber);
    const phoneChanged = phoneNumber !== formatMobilePhoneNumber(originalPhoneNumber);

    setIsUpdating(true);
    try {
      await participantService.update(selectedEventId, editingId, {
        bibNumber,
        chipNumber,
        name: editForm.name.trim(),
        gender: collectFlags.collectGender && editForm.gender.trim() ? editForm.gender.trim() : undefined,
        category: category.categoryName,
        group: editForm.group,
        birth: collectFlags.collectBirth && editForm.birth ? editForm.birth : undefined,
        // 빈 문자열을 보내면 백엔드가 전화번호를 지운다.
        phoneNumber: phoneMasked || !phoneChanged ? undefined : phoneNumber,
      });
      await queryClient.invalidateQueries({ queryKey: ['results', selectedEventId] });
      setIsConfirmGroupChangeOpen(false);
      setIsEditOpen(false);
      setRefreshKey((key) => key + 1);
    } catch (error) {
      showError(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const columns: DataTableColumn<Participant>[] = canUpdateParticipants
    ? [
        ...baseColumns,
        {
          key: '_edit',
          header: '',
          align: 'right',
          width: 56,
          render: (row) => (
            <IconButton
              size="small"
              aria-label="참가자 수정"
              sx={{ color: '#94a3b8', '&:hover': { color: '#2563eb' } }}
              onClick={() => openEdit(row)}
            >
              <EditIcon />
            </IconButton>
          ),
        },
      ]
    : baseColumns;

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  if (selectedEventId == null) {
    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <p className="page-section">Event</p>
          <h1>참가자 관리</h1>
          <Box sx={{ mt: 3, color: '#8a94a6', fontSize: 14 }}>
            선택된 대회가 없습니다. 대회 목록에서 대회를 먼저 선택해 주세요.
          </Box>
        </div>
      </section>
    );
  }

  return (
    <section className="page-placeholder">
      <div className="page-content page-content-wide">
        <p className="page-section">Event</p>
        <h1>참가자 관리</h1>
        <p className="page-description">참가자 정보를 조회하고 등록, 수정, 삭제합니다.</p>

        {/* 툴바: 검색(좌) + 데이터 추가/추출(우) */}
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: '1 1 260px',
              maxWidth: 540,
            }}
          >
            <Box sx={{ alignItems: 'center', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, display: 'flex', flex: 1, minWidth: 0, px: 1.5, py: 0.5 }}>
              <InputBase
                fullWidth
                placeholder="배번호/Tid/이름/전화번호 뒤 4자리"
                value={draftKeyword}
                onChange={(e) => setDraftKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applySearch();
                }}
                sx={{ fontSize: 13 }}
              />
            </Box>
            <Button disabled={isLoading} onClick={applySearch} size="small" sx={{ flexShrink: 0, minWidth: 72 }} variant="contained">검색</Button>
            <Button disabled={isLoading} onClick={resetSearch} size="small" sx={{ flexShrink: 0, minWidth: 72 }} variant="outlined">초기화</Button>
          </Box>

          {canManageParticipants && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" onClick={openAdd}>
                참가자 추가하기
              </Button>
              <Button size="small" variant="outlined" onClick={openImport}>
                파일로 추가하기
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={totalCount === 0 || isExporting}
                onClick={handleExport}
              >
                {isExporting ? '추출 중…' : 'csv 추출하기'}
              </Button>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mt: 2,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ fontSize: 13, color: '#8a94a6' }}>
            총 {totalCount.toLocaleString()}명
            {selectedKeys.length > 0 && ` · ${selectedKeys.length}명 선택됨`}
          </Box>
          {canManageParticipants && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={selectedKeys.length === 0}
                onClick={() => setIsConfirmDeleteOpen(true)}
              >
                선택 삭제
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                disableElevation
                disabled={totalCount === 0}
                onClick={() => setIsConfirmDeleteAllOpen(true)}
              >
                전체 삭제
              </Button>
            </Box>
          )}
        </Box>

        <div className="page-table">
          {isLoading ? (
            <LoadingState message="참가자 목록을 불러오는 중입니다." />
          ) : (
            <DataTable
              columns={columns}
              rows={participants}
              rowKey="id"
              selectable={canManageParticipants}
              selectedRowKeys={selectedKeys}
              onSelectionChange={setSelectedKeys}
              emptyMessage={
                appliedKeyword ? '검색 결과가 없습니다.' : '등록된 참가자가 없습니다.'
              }
            />
          )}

          {pageCount > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={pageCount}
                page={page + 1}
                onChange={(_, next) => setPage(next - 1)}
                size="small"
                color="primary"
              />
            </Box>
          )}
        </div>
      </div>

      <Dialog
        open={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>참가자 삭제</DialogTitle>
        <DialogContent>
          <Box sx={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
            선택한 참가자 {selectedKeys.length}명을 삭제할까요?
            <br />
            경기 기록이 있는 참가자는 삭제되지 않으며, 삭제 결과가 표시됩니다.
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDeleteOpen(false)}>취소</Button>
          <Button color="error" variant="contained" disableElevation onClick={handleDeleteSelected}>
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isConfirmDeleteAllOpen}
        onClose={() => setIsConfirmDeleteAllOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: '#b91c1c' }}>
          전체 참가자 삭제
        </DialogTitle>
        <DialogContent>
          <Box sx={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
            이 대회의 <b>전체 참가자 {totalCount.toLocaleString()}명</b>을 삭제합니다. 되돌릴 수 없습니다.
            <br />
            경기 기록이 있는 참가자는 삭제되지 않으며, 삭제 결과가 표시됩니다.
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDeleteAllOpen(false)}>취소</Button>
          <Button color="error" variant="contained" disableElevation onClick={handleDeleteAll}>
            전체 삭제
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isAddOpen} onClose={blockBackdropClose(() => setIsAddOpen(false))} maxWidth="sm" fullWidth>
        <FormDialogTitle onClose={() => setIsAddOpen(false)} sx={{ fontSize: 16, fontWeight: 700 }}>
          참가자 추가
        </FormDialogTitle>
        <DialogContent dividers>
          {categories.length === 0 && (
            <Box sx={{ mb: 2, fontSize: 13, color: '#b45309' }}>
              등록된 종목이 없습니다. 종목 관리에서 종목·그룹을 먼저 추가해 주세요.
            </Box>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 0.5 }}>
            <TextField
              label="배번호"
              size="small"
              type="number"
              value={addForm.bibNumber}
              error={Boolean(addFieldErrors.bibNumber)}
              helperText={addFieldErrors.bibNumber}
              onChange={(e) => updateAddField('bibNumber', e.target.value)}
            />
            <TextField
              label="칩번호"
              size="small"
              type="number"
              value={addForm.chipNumber}
              error={Boolean(addFieldErrors.chipNumber)}
              helperText={addFieldErrors.chipNumber}
              onChange={(e) => updateAddField('chipNumber', e.target.value)}
            />
            <TextField
              label="이름"
              size="small"
              value={addForm.name}
              error={Boolean(addFieldErrors.name)}
              helperText={addFieldErrors.name}
              onChange={(e) => updateAddField('name', e.target.value)}
            />
            {collectFlags.collectGender && (
              <TextField
                label="성별"
                size="small"
                required
                value={addForm.gender}
                error={Boolean(addFieldErrors.gender)}
                helperText={addFieldErrors.gender}
                onChange={(e) => updateAddField('gender', e.target.value)}
              />
            )}
            <TextField
              label="종목"
              size="small"
              select
              value={addForm.categoryId === '' ? '' : String(addForm.categoryId)}
              error={Boolean(addFieldErrors.categoryId)}
              helperText={addFieldErrors.categoryId}
              onChange={(e) => {
                updateAddField('categoryId', e.target.value === '' ? '' : Number(e.target.value));
                updateAddField('group', '');
              }}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={String(category.id)}>
                  {category.categoryName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="그룹"
              size="small"
              select
              value={addForm.group}
              disabled={selectedAddCategory == null}
              error={Boolean(addFieldErrors.group)}
              helperText={addFieldErrors.group}
              onChange={(e) => updateAddField('group', e.target.value)}
            >
              {addGroupOptions.map((group) => (
                <MenuItem key={group.groupId} value={group.groupName}>
                  {group.groupName}
                </MenuItem>
              ))}
            </TextField>
            {collectFlags.collectBirth && (
              <TextField
                label="생년월일"
                size="small"
                type="date"
                required
                inputProps={{ min: MIN_BIRTH_DATE, max: MAX_BIRTH_DATE }}
                InputLabelProps={{ shrink: true }}
                value={addForm.birth}
                error={Boolean(addFieldErrors.birth)}
                helperText={addFieldErrors.birth}
                onChange={(e) => updateAddField('birth', e.target.value)}
              />
            )}
            <TextField
              inputProps={{ inputMode: 'numeric', maxLength: 13 }}
              label="전화번호 (선택)"
              size="small"
              placeholder="010-1234-5678"
              value={addForm.phoneNumber}
              error={Boolean(addFieldErrors.phoneNumber)}
              helperText={addFieldErrors.phoneNumber}
              onChange={(e) => updateAddField('phoneNumber', formatMobilePhoneNumber(e.target.value))}
            />
          </Box>
          <Box sx={{ mt: 1.5, fontSize: 12, color: '#94a3b8' }}>
            나이그룹은 생년월일 기준으로 자동 계산됩니다.
          </Box>
          {addError && (
            <Box sx={{ mt: 1.5, fontSize: 13, color: '#dc2626' }}>{addError}</Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddOpen(false)}>취소</Button>
          <Button variant="contained" disableElevation onClick={handleCreate}>
            추가
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isImportOpen} onClose={blockBackdropClose(() => setIsImportOpen(false))} maxWidth="sm" fullWidth>
        <FormDialogTitle onClose={() => setIsImportOpen(false)} sx={{ fontSize: 16, fontWeight: 700 }}>
          파일로 추가
        </FormDialogTitle>
        <DialogContent dividers>
          <Box
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleImportDrop}
            sx={{
              border: '1px dashed #cbd5e1',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              backgroundColor: '#fbfcfe',
            }}
          >
            {importFile ? (
              <Box sx={{ fontSize: 14, color: '#334155' }}>
                <b>{importFile.name}</b>
                <Box sx={{ fontSize: 12, color: '#94a3b8', mt: 0.5 }}>
                  {formatFileSize(importFile.size)}
                </Box>
              </Box>
            ) : (
              <Box sx={{ fontSize: 13, color: '#8a94a6', lineHeight: 1.6 }}>
                첨부할 파일을 여기에 끌어다 놓거나,
                <br />
                파일 선택 버튼을 눌러 직접 선택해 주세요.
              </Box>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              hidden
              onChange={(e) => pickImportFile(e.target.files?.[0])}
            />
            <Button
              variant="contained"
              size="small"
              disableElevation
              sx={{ mt: 2 }}
              onClick={() => fileInputRef.current?.click()}
            >
              파일 선택
            </Button>
          </Box>

          <Box sx={{ mt: 1.5, fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>
            csv 또는 xlsx 파일. 열: 배번호 · 칩번호 · 이름 · 성별 · 종목 · 그룹 · 생년월일 · 전화번호
            <br />
            (나이그룹은 생년월일로 자동 계산됩니다.)
          </Box>

          {importResult && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ fontSize: 14, fontWeight: 700, color: '#334155' }}>
                성공 {importResult.insertedCount}건 · 실패 {importResult.failedCount}건
              </Box>
              {importResult.failedCount > 0 && (
                <Box
                  sx={{
                    mt: 1,
                    maxHeight: 180,
                    overflowY: 'auto',
                    border: '1px solid #fecaca',
                    backgroundColor: '#fef2f2',
                    borderRadius: 1,
                    p: 1.5,
                    fontSize: 12,
                    color: '#b91c1c',
                    lineHeight: 1.7,
                  }}
                >
                  {importResult.fails.map((fail) => (
                    <div key={fail.failedNum}>{formatImportFailure(fail)}</div>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsImportOpen(false)}>닫기</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={importFile == null || isImporting}
            onClick={handleImport}
          >
            {isImporting ? '업로드 중…' : '추가하기'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isEditOpen}
        onClose={blockBackdropClose(() => {
          if (!isUpdating && !isConfirmGroupChangeOpen) setIsEditOpen(false);
        })}
        maxWidth="sm"
        fullWidth
      >
        <FormDialogTitle
          onClose={() => {
            if (!isUpdating && !isConfirmGroupChangeOpen) setIsEditOpen(false);
          }}
          sx={{ fontSize: 16, fontWeight: 700 }}
        >
          참가자 수정
        </FormDialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 0.5 }}>
            <TextField
              label="배번호"
              size="small"
              type="number"
              value={editForm.bibNumber}
              onChange={(e) => updateEditField('bibNumber', e.target.value)}
            />
            <TextField
              label="칩번호"
              size="small"
              type="number"
              value={editForm.chipNumber}
              onChange={(e) => updateEditField('chipNumber', e.target.value)}
            />
            <TextField
              label="이름"
              size="small"
              value={editForm.name}
              onChange={(e) => updateEditField('name', e.target.value)}
            />
            {collectFlags.collectGender && (
              <TextField
                label="성별"
                size="small"
                value={editForm.gender}
                onChange={(e) => updateEditField('gender', e.target.value)}
              />
            )}
            <TextField
              label="종목"
              size="small"
              select
              value={editForm.categoryId === '' ? '' : String(editForm.categoryId)}
              onChange={(e) => {
                updateEditField('categoryId', e.target.value === '' ? '' : Number(e.target.value));
                updateEditField('group', '');
              }}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={String(category.id)}>
                  {category.categoryName}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="그룹"
              size="small"
              select
              value={editForm.group}
              disabled={selectedEditCategory == null}
              onChange={(e) => updateEditField('group', e.target.value)}
            >
              {editGroupOptions.map((group) => (
                <MenuItem key={group.groupId} value={group.groupName}>
                  {group.groupName}
                </MenuItem>
              ))}
            </TextField>
            {collectFlags.collectBirth && (
              <TextField
                label="생년월일"
                size="small"
                type="date"
                inputProps={{ min: MIN_BIRTH_DATE, max: MAX_BIRTH_DATE }}
                InputLabelProps={{ shrink: true }}
                value={editForm.birth}
                onChange={(e) => updateEditField('birth', e.target.value)}
              />
            )}
            <TextField
              inputProps={{ inputMode: 'numeric', maxLength: 13 }}
              label="전화번호 (선택)"
              size="small"
              placeholder="010-1234-5678"
              value={editForm.phoneNumber}
              onChange={(e) => updateEditField('phoneNumber', formatMobilePhoneNumber(e.target.value))}
            />
            <TextField label="나이그룹" size="small" value={editAgeGroup} disabled helperText="생년월일 기준 자동" />
          </Box>
          {editError && <Box sx={{ mt: 1.5, fontSize: 13, color: '#dc2626' }}>{editError}</Box>}
        </DialogContent>
        <DialogActions>
          <Button disabled={isUpdating} onClick={() => setIsEditOpen(false)}>취소</Button>
          <Button variant="contained" disableElevation disabled={isUpdating} onClick={handleUpdate}>
            {isUpdating ? '저장 중…' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isConfirmGroupChangeOpen}
        onClose={() => { if (!isUpdating) setIsConfirmGroupChangeOpen(false); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>그룹 변경 및 기록 재처리</DialogTitle>
        <DialogContent>
          <Box sx={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
            그룹을 <b>{originalGroup}</b>에서 <b>{editForm.group}</b>으로 변경할까요?
            <br />
            새 그룹의 건타임으로 원시 데이터를 다시 판정하고 자동 기록을 재계산합니다.
            수동 입력 기록은 보존되며, 확정된 완주·중도포기·출발안함 상태는 재검토가 필요합니다.
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={isUpdating} onClick={() => setIsConfirmGroupChangeOpen(false)}>취소</Button>
          <Button variant="contained" disableElevation disabled={isUpdating} onClick={() => void submitUpdate()}>
            {isUpdating ? '저장 중…' : '변경 및 재처리'}
          </Button>
        </DialogActions>
      </Dialog>

      {errorToast}
      <Snackbar
        open={deleteNotice !== null}
        autoHideDuration={5000}
        onClose={() => setDeleteNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={deleteNotice?.severity ?? 'info'}
          variant="filled"
          onClose={() => setDeleteNotice(null)}
          sx={{ width: '100%' }}
        >
          {deleteNotice?.message}
        </Alert>
      </Snackbar>
    </section>
  );
}

export default ParticipantsPage;
